import AsyncStorage from "@react-native-async-storage/async-storage";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { Platform, ScrollView } from "react-native";
import type { ScrollViewProps as RNScrollViewProps } from "react-native";
import Animated from "react-native-reanimated";
import { useFragment } from "react-relay";

import {
  COLUMN_MIN_WIDTH_PX,
  TIMEBAR_WIDTH_PX,
} from "@whoosh/design-system/src/apps/PatronApp/screens/multiSlotBooking";
import { Box, HStack } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import {
  selectedCoursesByFacility,
  selectedCourseIdsByFacility,
} from "../../../atoms/selectedCoursesByFacility";
import { useGetFirstLastTimeAcrossCoursesPatron } from "../hooks/useGetFirstLastTimeAcrossCoursesPatron";
import { ColumnHeaderRow } from "./ColumnHeaderRow";
import { CourseColumns } from "./CourseColumns";
import { CourseColumnsEmptyState } from "./CourseColumnsEmptyState";
import { TimeColumn } from "./TimeColumn";
import { BidirectionalStickyScroller_facilities$key } from "./__generated__/BidirectionalStickyScroller_facilities.graphql";

type BidirectionalStickyScrollerProps = {
  facilityRefs: BidirectionalStickyScroller_facilities$key;
  currentSlotId?: string;
  handleMultiSlotRowPress?: (slotId: string) => void;
  setBookingWizardSlotId?: (slotId: string) => void;
  setBookingWizardStartTime?: (startTime: string) => void;
  onVerticalScroll?: RNScrollViewProps["onScroll"];
};

const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

export const BidirectionalStickyScroller = ({
  facilityRefs,
  currentSlotId,
  handleMultiSlotRowPress,
  setBookingWizardSlotId,
  setBookingWizardStartTime,
  onVerticalScroll,
}: BidirectionalStickyScrollerProps) => {
  const facilities = useFragment(
    BidirectionalStickyScrollerFragmentString,
    facilityRefs
  );
  const facility = facilities[0];
  const courseNoun = getNounOverride(
    facility?.copyOverrides?.course?.plural,
    "Courses"
  );
  const courseIds = useAtomValue(
    selectedCourseIdsByFacility(facility?.id ?? "")
  );

  const setSelectedCourses = useSetAtom(
    selectedCoursesByFacility(facility?.id ?? "")
  );

  const isMobileLayout = useBreakpointValue({
    base: true,
    sm: true,
    md: false,
  });

  const resetSelectedCourses = useCallback(() => {
    const edges = facilities.flatMap(
      (facilityBatch) => facilityBatch?.courses?.edges ?? []
    );
    const checkedCourses: Record<string, boolean> = {};
    const courses = edges
      .map((edge) => edge?.node)
      .filter((node) => node !== null && typeof node !== "undefined");
    Object.values(courses).forEach((course) => {
      checkedCourses[course!.id] = true;
    });
    setSelectedCourses(checkedCourses);
  }, [facilities, setSelectedCourses]);

  useEffect(() => {
    if (!facility) {
      return;
    }

    // If there are no selected courses and no filters set in storage yet,
    // we will get no courses back even though there are no filters
    // so we reset the selected courses to all courses.

    const isWebAndNeedsReset =
      Platform.OS === "web" &&
      courseIds.length === 0 &&
      localStorage.getItem(`${facility.id}:selectedCoursesByFacility`) === null;
    if (isWebAndNeedsReset) {
      resetSelectedCourses();
    }

    const isMobileAndHasNoCourses =
      (Platform.OS === "ios" || Platform.OS === "android") &&
      courseIds.length === 0;
    if (isMobileAndHasNoCourses) {
      AsyncStorage.getItem(`${facility.id}:selectedCoursesByFacility`).then(
        (value) => {
          if (value === null) {
            resetSelectedCourses();
          }
        }
      );
    }
  }, [courseIds.length, facility, resetSelectedCourses]);

  const agenda = facilities
    .flatMap((facilityBatch) => facilityBatch?.agendas?.edges ?? [])
    .find((edge) => edge?.node)?.node;

  const firstAndLastTime = useGetFirstLastTimeAcrossCoursesPatron({
    facilityRefs: facilities,
  });

  const renderedCourseCount = facilities
    .flatMap((facilityBatch) => facilityBatch?.courses?.edges ?? [])
    .filter(
      (edge) => edge?.node?.id && courseIds.includes(edge.node.id)
    ).length;

  const horizontalContentWidth =
    isMobileLayout && renderedCourseCount > 1
      ? renderedCourseCount * COLUMN_MIN_WIDTH_PX
      : "100%";

  const headerRef = useRef<ScrollView>(null);

  if (!facility) {
    return null;
  }

  return courseIds.length ? (
    <AnimatedScrollView
      testID="outer-scrollview-vertical"
      stickyHeaderIndices={[0]}
      bounces={false}
      onScroll={onVerticalScroll}
      scrollEventThrottle={16}
    >
      <HStack>
        <Box width={TIMEBAR_WIDTH_PX} backgroundColor="surface-primary"></Box>
        <ScrollView
          scrollEnabled={false}
          showsHorizontalScrollIndicator={false}
          horizontal
          style={{ height: 50 }}
          ref={headerRef}
          testID="column-headers-row"
          bounces={false}
        >
          <ColumnHeaderRow
            facilityRefs={facilities}
            courseIds={courseIds}
            width={horizontalContentWidth}
          />
        </ScrollView>
      </HStack>

      <Box style={{ flexDirection: "row" }} width="100%">
        {agenda && firstAndLastTime?.firstTime && firstAndLastTime.lastTime && (
          <>
            <TimeColumn
              firstTime={firstAndLastTime.firstTime}
              lastTime={firstAndLastTime.lastTime}
            />
          </>
        )}

        <ScrollView
          horizontal
          style={{ flex: 1 }}
          scrollEventThrottle={1}
          onScroll={(e: any) => {
            if (headerRef.current) {
              headerRef.current.scrollTo({
                x: e.nativeEvent.contentOffset.x,
                animated: false,
              });
            }
          }}
        >
          <CourseColumns
            facilityRefs={facilities}
            courseIds={courseIds}
            width={horizontalContentWidth}
            currentSlotId={currentSlotId}
            handleMultiSlotRowPress={handleMultiSlotRowPress}
            setBookingWizardSlotId={setBookingWizardSlotId}
            setBookingWizardStartTime={setBookingWizardStartTime}
          />
        </ScrollView>
      </Box>
    </AnimatedScrollView>
  ) : (
    <CourseColumnsEmptyState
      venueLabel={courseNoun}
      onPress={resetSelectedCourses}
    />
  );
};

export const BidirectionalStickyScrollerFragmentString = graphql`
  fragment BidirectionalStickyScroller_facilities on Facility
  @relay(plural: true) {
    id
    name
    copyOverrides {
      course {
        plural
      }
    }
    ...useGetFirstLastTimeAcrossCoursesPatron_facilities
    ...ColumnHeaderRow_facilities
    ...CourseColumns_facilities
    courses(courseIds: $courseIds) {
      count
      edges {
        node {
          id
          ...SingleCourseColumn_course
        }
      }
    }
    agendas(dateGte: $date, dateLte: $date) {
      edges {
        node {
          id
        }
      }
    }
  }
`;
